import React from 'react';

/**
 * A two-or-three-way choice you switch by clicking, instead of opening a menu.
 *
 * Use it when the options are few, short, and worth seeing at a glance — a
 * billing interval, a view mode. A dropdown hides the alternative behind a
 * click and reads as heavier than the decision deserves; this shows both and
 * takes one tap to change.
 *
 * Use `Dropdown` instead once there are more than three options, or when the
 * labels are long enough to wrap.
 */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedToggleProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  /** Labelled by whatever introduces the control, for screen readers. */
  ariaLabel?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
}

function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = 'md',
  disabled = false,
  className = '',
}: SegmentedToggleProps<T>) {
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 ${
        disabled ? 'opacity-60' : ''
      } ${className}`}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={`rounded-md font-medium transition-colors ${pad} ${
              selected
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700 disabled:hover:text-gray-500'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedToggle;
